import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Col, Row } from "react-bootstrap";
import { getCurrentUser, getHomeStats, getMainHeader, setHomeAnchor } from "../../services/apiClient.js";
import { HomeChart } from "../home/HomeBand.jsx";
import MarketingCallsPanel from "./MarketingCallsPanel.jsx";
import WorkflowReport from "./WorkflowReport.jsx";

// RDR Main, live — the DASHBOARD header (Robert 2026-07-13): one composed
// panel, GPS-wide by design (the PM filter's authority starts at the worklist
// bar below it).
//   Left column:  Logged vs targets · Accepted · Quote Turnaround SLA (month
//                 to date) · marketing counters · the full by-day chart
//                 (always shown — Karen 2026-07-15).
//   Right column: the OPEN BOOK — live jobs by pipeline stage as ordered bars
//                 (a funnel, not a pie: stages have an order, and bars beat
//                 wedges for comparing 66 vs 91). Every bar clicks through to
//                 the search page filtered to that status.

const SHORT = {
  log_contact: "Log→Contact",
  contact_scheduled: "Contact→Sched",
  scheduled_inspected: "Sched→Insp",
  inspected_typed: "Insp→Typed",
  typed_checked: "Typed→Checked",
  checked_sent: "Checked→Sent",
  inspected_sent: "Insp→Sent",
};

function Stat({ label, n, target = null }) {
  const missed = target != null && n < target;
  return (
    <div className="dashboard-stat text-nowrap">
      <div className="dashboard-stat-label text-muted text-uppercase">
        {label}
      </div>
      <div className="fs-4 fw-semibold lh-1">
        <span className={missed ? "text-danger" : ""}>{n}</span>
        {target != null ? <span className="text-muted fw-normal" style={{ fontSize: "0.8rem" }}> / {target}</span> : null}
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <span className="dashboard-section-label text-uppercase fw-semibold small text-muted align-self-center" style={{ letterSpacing: "0.06em" }}>
      {children}
    </span>
  );
}

// The open book: one row per pipeline stage, bar length = share of the largest
// stage, count labelled. Single hue (theme primary) — the rows carry identity.
function OpenBook({ book, breakdown = [] }) {
  const navigate = useNavigate();
  if (!book?.length) return null;
  const total = book.reduce((s, b) => s + b.n, 0);
  const max = Math.max(1, ...book.map((b) => b.n));
  return (
    <div className="open-book h-100 d-flex flex-column">
      <div className="text-muted text-uppercase" style={{ fontSize: "0.68rem", letterSpacing: "0.06em" }}>
        Open jobs
      </div>
      <div className="display-6 fw-semibold lh-1 mb-2">{total}</div>
      {book.map((b) => (
        <div
          key={b.code}
          role="button"
          className="open-book-row d-flex align-items-center column-gap-2 py-1"
          data-hint={`Open the claim search filtered to ${b.name}`}
          onClick={() => navigate(`/claims/search?status=${b.code}`)}
        >
          <span className="small text-muted text-nowrap" style={{ width: 72 }}>{b.name}</span>
          <div className="flex-grow-1">
            <div className="open-book-bar" style={{ width: `${Math.max(2, (b.n / max) * 100)}%` }} />
          </div>
          <span className="small fw-semibold text-nowrap" style={{ width: 34, textAlign: "right" }}>{b.n}</span>
        </div>
      ))}
      {/* The Accepted bar broken out (Robert 2026-08-05). These sum to it
          exactly, so they are indented sub-rows of that bar rather than another
          line on the left column — which is what makes them free: the panel no
          longer stretches, so this fills space that was empty anyway. */}
      {breakdown.length > 0 ? (
        <div className="ps-2 mt-1" style={{ borderLeft: "2px solid var(--bs-border-color)" }}>
          {breakdown.map((b) => (
            <div
              key={b.name}
              role="button"
              className="open-book-row d-flex align-items-center column-gap-2"
              data-hint={`Open the claim search filtered to Accepted — ${b.name}`}
              onClick={() => navigate(`/claims/search?status=A&accepted_status=${encodeURIComponent(b.name)}`)}
            >
              <span className="small text-muted flex-grow-1 text-nowrap">{b.name}</span>
              <span className="small text-nowrap" style={{ width: 34, textAlign: "right" }}>{b.n}</span>
            </div>
          ))}
        </div>
      ) : null}
      <div className="small text-muted mt-auto pt-1">Click a stage to open it in the search.</div>
    </div>
  );
}

// Exported separately: the header stays visible even when the reports master
// switch is OFF (Robert 2026-07-13 — "it's pretty cool"); only the worklists
// below it are gated.
export function DashboardHeader() {
  const [header, setHeader] = useState(null);
  const [homeStats, setHomeStats] = useState(null);
  const [me, setMe] = useState(null);

  useEffect(() => {
    getMainHeader().then(setHeader).catch(() => {});
    getHomeStats().then(setHomeStats).catch(() => {});
    getCurrentUser().then(setMe).catch(() => {});
  }, []);

  async function changeAnchor(value) {
    try {
      await setHomeAnchor(value || "latest");
      setHomeStats(await getHomeStats());
    } catch {
      /* the admin demo control failing is not worth an alert */
    }
  }

  return (
    <>
      {/* RESERVE THE SPACE WHILE LOADING. This panel used to render nothing at
          all until its data arrived, then appear at full height and shove the
          worklists below it down the page (Robert, 16 Aug). Making the header
          faster helps it win the race, but it does not FIX this — a slow network
          or a cold cache would reorder them again, and then the jump is back.
          An empty panel of roughly the right height cannot jump, whoever wins. */}
      {!header ? (
        <div className="placeholder-panel" style={{ minHeight: 420 }} aria-busy="true" />
      ) : (
        <Card body className="dashboard-overview">
          {/* align-items-start: the two columns are independent panels, not a
              pair that must match height. Stretching made Open Jobs as tall as
              the chart column, so every line added on the left grew the empty
              space on the right (Robert 2026-08-05). */}
          <Row className="g-4 align-items-start">
            <Col lg={8}>
              <div className="dashboard-metrics mb-2">
                <div className="dashboard-stat-group">
                  <SectionLabel>Logged</SectionLabel>
                  <Stat label="Today" n={header.logged.today.n} target={header.logged.today.target} />
                  <Stat label="This Week" n={header.logged.week.n} target={header.logged.week.target} />
                  <Stat label="This Month" n={header.logged.month.n} target={header.logged.month.target} />
                </div>
                <div className="dashboard-stat-group">
                  <SectionLabel>Accepted</SectionLabel>
                  <Stat label="Today" n={header.accepted.today} />
                  <Stat label="This Week" n={header.accepted.week} />
                  <Stat label="This Month" n={header.accepted.month} />
                </div>
              </div>
              <div className="dashboard-sla d-flex flex-wrap column-gap-4 row-gap-1 pb-2 mb-2 border-bottom">
                {/* Karen's naming (2026-07-15): the office knows this as quote
                    turnaround. Still month-to-date. */}
                <SectionLabel>Quote Turnaround SLA</SectionLabel>
                {header.sla.summary.map((s) => {
                  const col = header.sla.columns.find((c) => c.key === s.key);
                  return (
                    <div key={s.key} className="text-nowrap">
                      <div className="text-muted text-uppercase" style={{ fontSize: "0.62rem", letterSpacing: "0.06em" }}>
                        {SHORT[s.key]}
                      </div>
                      <div className={`fs-5 fw-semibold lh-1 ${s.pct == null ? "text-muted" : s.pct >= col.target ? "text-success" : "text-danger"}`}>
                        {s.pct == null ? "—" : `${s.pct}%`}
                      </div>
                    </div>
                  );
                })}
              </div>
              <MarketingCallsPanel embedded />
              {/* The by-day chart just displays, filling the column (Karen's
                  feedback 2026-07-15 — no show/hide). It measures its
                  container, so it sizes itself here. */}
              <div className="dashboard-chart border-top pt-2 mt-1">
                <HomeChart stats={homeStats} isAdmin={!!me?.is_admin} onAnchorChange={changeAnchor} />
              </div>
            </Col>
            <Col lg={4} className="border-start-lg">
              <OpenBook book={header.open_book} breakdown={header.accepted_breakdown} />
            </Col>
          </Row>
        </Card>
      )}
    </>
  );
}

function RdrMainReport() {
  return (
    <>
      <DashboardHeader />
      <WorkflowReport group="main" />
    </>
  );
}

export default RdrMainReport;
